import React from 'react';
import { Scorecard } from 'talent_matching/types';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import {
  NoPaddingStrongYesIcon as StrongYesIcon,
  NoPaddingYesIcon as YesIcon,
  NoPaddingNoIcon as NoIcon,
  NoPaddingStrongNoIcon as StrongNoIcon,
  NoPaddingNoDecisionIcon as NoDecisionIcon,
} from 'shared/components/scorecard_icons/base_icons';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import styled from 'styled-components';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix('talent_matching.feedback');

const DISPLAYABLE_FEEDBACKS = 4;

const ICON_MAP = {
  definitely_not: StrongNoIcon,
  no: NoIcon,
  yes: YesIcon,
  strong_yes: StrongYesIcon,
  no_decision: NoDecisionIcon,
};

const IconWrapper = styled(Stack)`
  .strong_yes,
  .yes,
  .no,
  .strong_no,
  .no-decision {
    height: 20px;
    width: 20px;
  }
`;

export const FeedbackItem = ({
  scorecard,
  showYou = true,
}: {
  scorecard: Scorecard;
  showYou?: boolean;
}) => {
  const key = scorecard.decision.key;
  const label = t(`options.${key}`);
  const Icon = ICON_MAP[key as keyof typeof ICON_MAP];

  return (
    <Stack direction="row" spacing={0.5}>
      <IconWrapper sx={{ width: '24px' }}>
        <Icon title={label} />
      </IconWrapper>
      <Stack direction="row" spacing={0.5}>
        <SecondaryBody>{label}</SecondaryBody>
        {showYou && scorecard.is_current_user && (
          <SecondaryBody>{t('you')}</SecondaryBody>
        )}
      </Stack>
    </Stack>
  );
};

type Props = {
  scorecards: Scorecard[];
  onShowMore?: () => void;
};

const BaseFeedbackCell = ({ scorecards, onShowMore }: Props) => {
  return (
    <Stack
      sx={{
        padding: 1,
        paddingLeft: 0,
        height: '100%',
      }}
    >
      {scorecards.length === 0 ? (
        <SecondaryBody>{t('no_feedback')}</SecondaryBody>
      ) : (
        <Stack spacing={1}>
          {scorecards
            ?.slice(0, DISPLAYABLE_FEEDBACKS)
            .map((scorecard: Scorecard) => {
              return <FeedbackItem key={scorecard.id} scorecard={scorecard} />;
            })}
          {onShowMore && scorecards.length > DISPLAYABLE_FEEDBACKS && (
            <Grid>
              <TertiaryButton onClick={onShowMore} size="md">
                {t('show_more')}
              </TertiaryButton>
            </Grid>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default BaseFeedbackCell;
